import { useQuery } from "@tanstack/react-query";

import { API } from "../constant/api";
import useAuthAxios from "../utils/baseAxios";
import { useBusinesses } from "./use-businesses";

/**
 * The full record for the merchant's current business — the detail behind the
 * id that `useBusinesses` resolves. Settings tabs (business, banks) and the
 * payments page all need it, so the query key is shared and the round trip is
 * paid once.
 */
export const useBusiness = () => {
  const axiosInstance = useAuthAxios();
  const { businessId, isLoading: loadingBusinesses } = useBusinesses();

  const query = useQuery({
    queryKey: ["get-business", businessId],
    queryFn: async () => {
      const response = await axiosInstance.get(
        API.getBusiness(businessId!, "", "")
      );
      return response.data.data;
    },
    enabled: !!businessId,
    retry: 1,
  });

  const business = query.data?.business;

  const isRegistered = business?.type === "REGISTERED";
  const isBranch = !!business?.parentBusinessId;
  const parentBusinessId = business?.parentBusinessId as string | undefined;
  // The admin app writes APPROVED; its own comments also mention VERIFIED, so
  // accept both rather than depending on which one lands.
  const isVerified = ["APPROVED", "VERIFIED"].includes(
    String(business?.status ?? "").toUpperCase()
  );

  return {
    ...query,
    businessId,
    business,
    banks: business?.banks,
    isRegistered,
    isBranch,
    parentBusinessId,
    isVerified,
    /**
     * Whether registration *documents* can still be changed: a business can
     * submit and resubmit until it is both registered and verified. Starter
     * businesses can upgrade, and a REJECTED business can fix its submission
     * and try again; only an approved registered business is frozen.
     *
     * This does NOT gate the Business details tab — logo, size, address and
     * description are operational details every business can edit at any time.
     */
    canEdit: !(isRegistered && isVerified),
    isLoading: loadingBusinesses || query.isLoading,
  };
};
